import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { FlatNode } from "screens/call/callscript-graph-to-nested";
import { calculateMatchFuzzy } from "screens/call/text-matching-fuzzy";

import type { SocketTranscriptPayload } from "./use-call-audio";

type CallscriptProgressState = {
  currentNodeId: number | null;
  completedNodeIds: Set<number>;
};

type CallscriptProgressActions = {
  setCurrentNode: (nodeId: number) => void;
  markNodeCompleted: (nodeId: number) => void;
  onTranscript: (transcript: SocketTranscriptPayload) => void;
  resetProgress: () => void;
  scrollToCurrentNode: () => void;
  endScript: () => void;
};

const TRANSCRIPT_DEBOUNCE_MS = 2000;
const SCROLL_DELAY_MS = 200;
const LOOKBACK = 100;
const MAX_TRACKED = 5;

export const useCallscriptProgress = (
  flattenedGraph: FlatNode[] | null,
  setAutoFoundNodeId: CallableFunction,
  setVisitedGroups: CallableFunction,
): CallscriptProgressState & CallscriptProgressActions => {
  const [state, setState] = useState<CallscriptProgressState>({
    currentNodeId: null,
    completedNodeIds: new Set(),
  });

  const transcriptBufferRef = useRef<string>("");
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const trackedWordCount = useRef<number>(0);
  const currentNodeIdRef = useRef<number | null>(state.currentNodeId);
  currentNodeIdRef.current = state.currentNodeId;

  const initialNodeId = useMemo(() => {
    if (!flattenedGraph) {
      return null;
    }

    const startNode = flattenedGraph.find((node) => node.type === "start");
    if (!startNode || startNode.childIds.length === 0) {
      return flattenedGraph.find((node) => node.type !== "start")?.id || null;
    }

    return startNode.childIds[0] || null;
  }, [flattenedGraph]);

  useEffect(() => {
    if (initialNodeId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setState((prev) => ({
        ...prev,
        currentNodeId: initialNodeId,
      }));
    }
  }, [initialNodeId]);

  const checkNodeMatch = useCallback(
    (nodeId: number, transcript: string): boolean => {
      if (!flattenedGraph) {
        return false;
      }

      const node = flattenedGraph.find((n) => n.id === nodeId);
      if (!node?.phrases?.length) {
        return false;
      }

      return node.phrases.some((phrase) => calculateMatchFuzzy(transcript, phrase));
    },
    [flattenedGraph],
  );

  const onTranscript = useCallback(
    (transcript: SocketTranscriptPayload) => {
      if (!(currentNodeIdRef.current && flattenedGraph)) {
        return;
      }

      if (transcript.channel !== undefined && transcript.channel !== 0) {
        return;
      }

      const incomingText = transcript.text.trim();
      transcriptBufferRef.current += ` ${incomingText}`;
      trackedWordCount.current += incomingText.split(/\s+/).filter(Boolean).length;

      const words = transcriptBufferRef.current.trim().split(/\s+/);
      const concattedTranscript = words.slice(-LOOKBACK).join(" ");
      transcriptBufferRef.current = concattedTranscript;

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }

      if (trackedWordCount.current >= MAX_TRACKED) {
        trackedWordCount.current = 0;
        const nodeId = currentNodeIdRef.current;
        if (nodeId && checkNodeMatch(nodeId, concattedTranscript)) {
          setAutoFoundNodeId(nodeId);
        }
        return;
      }

      debounceTimerRef.current = setTimeout(() => {
        trackedWordCount.current = 0;
        const nodeId = currentNodeIdRef.current;
        if (!nodeId) {
          return;
        }

        if (checkNodeMatch(nodeId, concattedTranscript)) {
          setAutoFoundNodeId(nodeId);
        }
      }, TRANSCRIPT_DEBOUNCE_MS);
    },
    [flattenedGraph, checkNodeMatch, setAutoFoundNodeId],
  );

  const setCurrentNode = useCallback(
    (nodeId: number) => {
      const currentNode = flattenedGraph?.find((n) => n.id === currentNodeIdRef.current);
      const { groupId } = currentNode ?? {};
      if (groupId) {
        setVisitedGroups((visited: Set<number>) => new Set([...visited, groupId]));
      }
      transcriptBufferRef.current = "";
      trackedWordCount.current = 0;
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      setState((prev) => ({ ...prev, currentNodeId: nodeId }));
    },
    [flattenedGraph, setVisitedGroups],
  );

  const markNodeCompleted = useCallback((nodeId: number) => {
    setState((prev) => ({
      ...prev,
      completedNodeIds: new Set([...prev.completedNodeIds, nodeId]),
    }));
  }, []);

  const resetProgress = useCallback(() => {
    setState({
      currentNodeId: initialNodeId,
      completedNodeIds: new Set(),
    });
    transcriptBufferRef.current = "";
    trackedWordCount.current = 0;
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, [initialNodeId]);

  const endScript = useCallback(() => {
    setState((prev) => ({ ...prev, currentNodeId: null }));
  }, []);

  const scrollToCurrentNode = useCallback(() => {
    if (!state.currentNodeId) {
      return;
    }

    const element = document.getElementById(`node-${state.currentNodeId}`);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      if (element.tabIndex >= 0 || element.tagName === "BUTTON") {
        element.focus();
      }
    }
  }, [state.currentNodeId]);

  useEffect(() => {
    if (state.currentNodeId) {
      const timer = setTimeout(scrollToCurrentNode, SCROLL_DELAY_MS);
      return () => clearTimeout(timer);
    }
  }, [state.currentNodeId, scrollToCurrentNode]);

  useEffect(
    () => () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    },
    [],
  );

  return {
    ...state,
    setCurrentNode,
    markNodeCompleted,
    onTranscript,
    resetProgress,
    scrollToCurrentNode,
    endScript,
  };
};
